import React, { useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Home, Calendar, Wallet, User, LogOut, Sparkles } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const TABS = [
  { to: '/partner/dashboard', icon: Home, label: 'Home', testId: 'partner-nav-dashboard' },
  { to: '/partner/bookings', icon: Calendar, label: 'Bookings', testId: 'partner-nav-bookings' },
  { to: '/partner/earnings', icon: Wallet, label: 'Earnings', testId: 'partner-nav-earnings' },
  { to: '/partner/profile', icon: User, label: 'Profile', testId: 'partner-nav-profile' },
];

function NavTab({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          'flex-1 flex flex-col items-center justify-center py-2 text-[11px] font-medium transition-colors',
          isActive
            ? 'text-rose-600 [&_.bm-tab-icon]:bg-rose-100'
            : 'text-slate-500 hover:text-slate-900',
        )
      }
    >
      <div className="bm-tab-icon h-8 w-8 rounded-xl flex items-center justify-center mb-0.5">
        <Icon className="h-4 w-4" />
      </div>
      {label}
    </NavLink>
  );
}

export default function PartnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/partner/login', { replace: true });
  }, [logout, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="leading-tight">
              <h1 className="text-sm font-bold text-slate-900">BeautyMateUK</h1>
              <p className="text-[10px] text-slate-500">Partner · {user?.name || ''}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            data-testid="partner-logout"
            aria-label="Sign out"
            className="text-slate-500 hover:text-slate-900"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-md mx-auto w-full px-4 py-4 pb-24">
        <Outlet />
        <div className="pt-6 text-center text-[10px] text-slate-400 space-x-3" data-testid="partner-legal-links">
          <NavLink to="/partner-terms" className="hover:text-slate-700">Partner Terms</NavLink>
          <NavLink to="/terms" className="hover:text-slate-700">Terms</NavLink>
          <NavLink to="/privacy" className="hover:text-slate-700">Privacy</NavLink>
          <NavLink to="/safety-policy" className="hover:text-slate-700">Safety</NavLink>
        </div>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-slate-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]">
        <div className="max-w-md mx-auto flex">
          {TABS.map((tab) => (
            <NavTab key={tab.to} {...tab} />
          ))}
        </div>
      </nav>
    </div>
  );
}
